import type {Page} from '@PosterWhiteboard/page/page.class';
import {GlobalPosterEditorJqueryElement} from '@Components/poster-editor/poster-editor.types';

export const addNewBlankPage = async (): Promise<void> => {
  const poster = window.posterEditor?.whiteboard;
  if (!poster) {
    return;
  }
  const currentPageHashedId = poster.getCurrentPageId();
  const pageOrder = poster.pages.pageOrder;
  const currentPageIndex = pageOrder.indexOf(currentPageHashedId);
  // When no current page is selected (indexOf === -1), append to the end instead of inserting at position 0,
  // which would cause addNewPage to read pagesInOrder[-1].background and crash.
  const targetOrder = currentPageIndex === -1 ? pageOrder.length : currentPageIndex + 1;
  await addNewPage(targetOrder);
};

export const addNewPage = async (order?: number): Promise<void> => {
  if (!window.posterEditor?.whiteboard?.pages) {
    throw Error('No poster found to add page. Initialize a poster first');
  }

  const pagesInOrder = window.posterEditor.whiteboard.pages.getPagesInOrder();
  const pageOrder = order ?? pagesInOrder?.length;

  if (pageOrder === window.PMW.MAX_NUMBER_OF_PAGES) {
    return;
  }

  const page = await window.posterEditor.whiteboard.pages.addPage(
    {
      background: pagesInOrder[pageOrder - 1].background?.toObject(),
    },
    {pageOrder}
  );
  scrollToPage(page.hashedID);
  page.poster.setCurrentPage(page.hashedID);
};

export const scrollToPage = (hashedId: string, animate = true): void => {
  const ANIMATION_DURATION = 300;
  const page = window.posterEditor?.whiteboard?.pages.getPage(hashedId);
  const pageOffsetTop = page?.pageContainer.offsetTop;
  const whiteboardContainer = window.posterEditor?.elements[GlobalPosterEditorJqueryElement.WHITEBOARD_CONTAINER];
  if (whiteboardContainer && typeof pageOffsetTop === 'number') {
    page?.refreshFullCanvas();
    if (animate) {
      whiteboardContainer.animate({scrollTop: pageOffsetTop}, ANIMATION_DURATION, () => {
        window.posterEditor?.whiteboard?.refreshAllVisibleCanvases();
      });
    } else {
      whiteboardContainer.scrollTop(pageOffsetTop);
      window.posterEditor?.whiteboard?.refreshAllVisibleCanvases();
    }
  }
};

export const addDuplicatedPage = async (pageToDuplicate: Page): Promise<void> => {
  const poster = window.posterEditor?.whiteboard;
  const pages = poster?.pages;
  if (!pages || !poster) {
    throw Error('No poster found to add page. Initialize a poster first');
  }

  if (pages.pageOrder.length >= window.PMW.MAX_NUMBER_OF_PAGES) {
    return;
  }

  // A duplicated page reuses the same Getty assets (identical hashedFilenames), so it can never raise the unique Getty count — only unique assets count toward the limit, so no limit check is needed here.
  const duplicatedPageObject = pageToDuplicate.getDuplicatedPageObject();

  const pageOrder = (pages.getPageOrder(pageToDuplicate.hashedID) ?? 0) + 1;
  const page = await pages.addPage(duplicatedPageObject, {pageOrder, updateRedux: true, immediatelySelectOnceAdded: true});

  scrollToPage(page.hashedID);
};
